export type ChatMessage = {
  id: string | number;
  time?: string;
  sender?: string;
  senderId?: string;
  reactions?: Array<{ user?: string; userId?: string; emoji?: string }>;
  [key: string]: unknown;
};

export function compareMessages(a: ChatMessage, b: ChatMessage): number {
  const ta = a.time ? new Date(a.time).getTime() : 0;
  const tb = b.time ? new Date(b.time).getTime() : 0;
  if (ta !== tb) return ta - tb;
  return String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
}

/** Merge lists by message id; live entries win over history for the same id. */
export function mergeMessagesById(
  live: ChatMessage[] | null | undefined,
  history: ChatMessage[] | null | undefined
): ChatMessage[] {
  const map = new Map<string, ChatMessage>();
  for (const msg of history || []) {
    if (msg?.id != null) map.set(String(msg.id), msg);
  }
  for (const msg of live || []) {
    if (msg?.id != null) map.set(String(msg.id), msg);
  }
  return Array.from(map.values()).sort(compareMessages);
}

export function createClientMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function sendFingerprint(text: string, replyId?: string | number | null): string {
  return `${String(text || '').trim()}|${replyId ?? ''}`;
}

/** Apply a confirmed text send to UI only when still on the sending thread. */
export function shouldApplyTextSendResult(
  sendingThreadId: string | number | null | undefined,
  activeThreadId: string | number | null | undefined,
  createdThreadId?: string | number | null
): boolean {
  if (sendingThreadId == null || activeThreadId == null) return false;
  const resolvedThreadId = createdThreadId ?? sendingThreadId;
  return String(resolvedThreadId) === String(activeThreadId)
    && String(sendingThreadId) === String(activeThreadId);
}

export function fileUploadSignature(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

export function isSelfMessage(
  message: { sender?: string; senderId?: string } | null | undefined,
  myId?: string | null,
  myName?: string | null
): boolean {
  if (!message) return false;
  if (myId && message.senderId) return String(message.senderId) === String(myId);
  if (myName && message.sender) return message.sender === myName;
  return false;
}

export function isSelfRecentItem(
  item: { sender?: string; senderId?: string } | null | undefined,
  myId?: string | null,
  myName?: string | null
): boolean {
  return isSelfMessage(item, myId, myName);
}

export function reactionKey(reaction: { user?: string; userId?: string; emoji?: string }): string {
  if (reaction.userId) return `id:${reaction.userId}:${reaction.emoji || ''}`;
  return `name:${reaction.user || ''}:${reaction.emoji || ''}`;
}

export function isMyReaction(
  reaction: { user?: string; userId?: string } | null | undefined,
  myId?: string | null,
  myName?: string | null
): boolean {
  if (!reaction) return false;
  if (myId && reaction.userId) return String(reaction.userId) === String(myId);
  if (myName && reaction.user) return reaction.user === myName;
  return false;
}
